import { CurrencyEnums, StyleEnums } from "models/enums";
import { ReactElement } from "react";
import { SelectionItem, SelectionKeyType } from "models/utilTypes/selectionTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { MoreActionMenuItem, MoreActionMenuItemType } from "models/propTypes/menuPropTypes";
import { IconDefinition } from "models/propTypes/iconPropTypes";
import { ComponentBaseProps } from "models/propTypes/basePropTypes";
import {
  GridAlignContentType,
  GridAlignItemsType,
  GridColumnCount,
  GridJustifyContentType,
} from "models/utilTypes/uiTypes";
import { DateRange, NumberRange } from "models/formTypes/util";
import { CommonEnums } from "models/enums";
import { GridDirection, GridSize, GridSpacing, GridWrap } from "@mui/material";
import { SelectionResultOut } from "models/propTypes/resultItemPropTypes";
import { BaseFormViewComponentProps, BaseFormComponentProps } from "models/propTypes/genericFormProps";
import { FormatDateOptions } from "react-intl";
import { User } from "models/propTypes/userProfilePropTypes";
import { MonthPickerQuickSelection } from "models/propTypes/datepickerPropTypes";

//#region MoreActions
export interface MoreActionsBaseProps {
  isFormValid?: boolean;
}

export interface MoreActionsProps extends MoreActionsBaseProps {
  /** Menu items */
  menuItems: MoreActionMenuItemType[];
  /** Handler to run on any menu item action success */
  onActionSuccess?: () => void;
  /** Disable showing success messages */
  disableSuccessMessages?: boolean;
  menuStateChangeCallback?: (isOpen: boolean) => void;
}

export interface SnoozeFormProps extends BaseFormComponentProps {
  entityId: number | string;
  type: WintSuperkollAPIModel.TodoType;
}

export type SnoozeData = {
  snoozeUntil: Date;
};

//#endregion

//#region PageFooter
export interface PageFooterProps extends ComponentBaseProps {
  /** List of actions, with button texts and with extra props in ActionButton, if there are any. */
  actions: ActionButton[];
  /** More actions component: More actions with UI if there are any. Mostly, a specific type of component that contains MoreActions component inside it. */
  moreActions?: ReactElement;
  /** Action to perform, on success. Ex: reload todos etc. */
  onActionSuccess?: (result?: any) => void;
  /** Show navigate back button. Ex: This is used when we have a footer, inside an entity view etc. */
  showNavigateBack?: boolean;
  /** Show help button. Ex: This is used when we have a footer, inside an wiard view etc. */
  showHelp?: boolean;
  onBackButtonClick?: () => void;
  onNextButtonClick?: () => void;
  columnsLeft?: GridColumnCount;
  isFormValid?: boolean;
  allowForReadOnlyUsers?: boolean;
  onActionError?: (result?: any) => void;
  disableSuccessMessages?: boolean;
}

export interface ActionButton {
  text: string | ReactElement;
  onClick?: () => Promise<any>;
  onActionSuccess?: (result?: any) => void;
  onActionError?: (result?: any) => void;
  type?: StyleEnums.DisplayType;
  isDisabled?: boolean;
  successMessage?: string;
  icon?: ReactElement;
  moreActions?: MoreActionMenuItemType[];
  validateButton?: boolean;
  hideWhenFormInvalid?: boolean;
  secondary?: boolean;
  showTooltip?: boolean;
  tooltipText?: string;
  testId?: string;
}

export interface MoreActionButtonProps extends StyledButtonProps {
  /** Handler to run on any menu item action success */
  onActionSuccess?: (result?: any) => void;
  /** Menu items */
  menuItems: MoreActionMenuItemType[];
  /** Menu placement. By default "top" */
  menuPlacement?: "top" | "bottom";
  /** Button placement. By default "center" */
  alignItems?: "flex-start" | "center" | "flex-end";
}

export interface StyledButtonProps extends ComponentBaseProps {
  /** Button sizes as per the design documentation */
  size?: "md" | "sm" | "xs";
  /** Change button color using the DisplayType enum */
  displayType?: StyleEnums.DisplayType;
  /** Button click handler */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** If button is disabled */
  disabled?: boolean;
  /** Icon to display after button text */
  endIcon?: React.ReactNode;
  /** Icon to display before button text */
  startIcon?: React.ReactNode;
  /** Button type */
  type?: "button" | "submit" | "reset";
  /** If a secondary button such as cancel button */
  secondary?: boolean;
  /** Auto focus on button */
  autoFocus?: boolean;
  /** Enable this to show that button action in progress */
  isSubmitting?: boolean;
  /** Button content */
  children?: React.ReactNode;
  /** Link to open, on click */
  href?: string;
}

export interface RoundButtonProps extends ComponentBaseProps {
  buttonKey?: any;
  /** Button sizes */
  size?: "lg" | "md" | "sm" | "xs";
  /** Change button color using the DisplayType enum */
  displayType?: StyleEnums.DisplayType;
  /** Button click handler */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** If button is disabled */
  disabled?: boolean;
  /** Button content */
  children?: React.ReactNode;
  /** Disable ripple effect */
  disableRipple?: boolean;
  /** Show additional text on button hover */
  hoverText?: string;
  /** Show additional text on button hover as a tooltip instead of expading button */
  hoverTextAsToolTip?: boolean;
}

export interface ToggleButtonProps {
  buttons: RoundButtonProps[];
  initialSelectionKey?: any;
  colorVariation?: "dark" | "light";
}

export interface ActionLinkButtonProps extends ComponentBaseProps {
  /** Button click handler */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** If button is disabled */
  disabled?: boolean;
  /** Icon to display after button text */
  endIcon?: React.ReactNode;
  /** Icon to display before button text */
  startIcon?: React.ReactNode;
  /** Button content */
  children?: React.ReactNode;
  /** Link to open, on click */
  href?: string;
}

export interface ActionLinksGroupProps extends ComponentBaseProps {
  links: QuickLink[];
  columns?: Record<"xs" | "sm" | "md" | "lg" | "xl", GridSize>;
}

export interface ActionLinksGroupButtonProps extends ComponentBaseProps {
  button: QuickLink;
  index: number;
}

export interface FooterTextProps extends ComponentBaseProps {
  icon?: IconDefinition;
  text: string;
}

//#endregion

//#region AsyncResultList
export interface AsyncResultListContainerProps {
  parentId: number;
  hasSearch?: boolean;
  getResultList: (searchText: string) => Promise<SelectionItem[] | undefined>;
  onResultItemSelect: (item: SelectionItem) => void;
  onResultItemRemove?: (id: SelectionKeyType) => void;
  selectedIds?: SelectionResultOut[];
  excludeIds?: SelectionKeyType[];
  searchInputPlaceholder?: string;
  getItemTemplate?: (data: any) => ReactElement;
}

export interface AsyncResultListProps extends AsyncResultListContainerProps {
  items?: SelectionItem[];
  searchText?: string;
  searchInputPlaceholder?: string;
  onSearchTextChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  getItemTemplate?: (data: any) => ReactElement;
  loading?: boolean;
}
//#endregion

//#region Page Titles
export interface EntityTitleProps extends ComponentBaseProps {
  status?: string;
  subTitle: string;
  title: string | ReactElement;
}

export interface FullTitleProps {
  title: string;
  subtitle?: string;
  titleIcon?: ReactElement;
}

export interface TableFullTitleProps extends FullTitleProps {}

export interface SimpleEntityTitleProps {
  status?: string;
  title: string;
}
//#endregion

export interface TagsProps extends ComponentBaseProps {
  /** Items to display in string format */
  tags: (string | ReactElement)[];
  toolTips?: string[];
  /** Enable maximum display count when having multiple tags. Extra items will be displayed on hover of the extra count */
  maxDisplayCount?: number;
  /** Which fill tier to use: "paper" (default) for a card/table row, "background" when the tag sits directly on the page background */
  surface?: "paper" | "background";
}

export interface DateRangeProps extends DateRange {
  separatorIcon?: IconDefinition;
}

export class StyledDateRange implements DateRange {
  start?: Date;
  end?: Date;

  constructor(start?: Date, end?: Date) {
    this.start = start;
    this.end = end;
  }
}

export interface NumberRangeProps extends NumberRange {
  separatorIcon?: IconDefinition;
  currencyCode?: CurrencyEnums.CurrencyCode;
}

export class StyledNumberRange implements NumberRange {
  highest?: number;
  lowest?: number;
  currencyCode?: CurrencyEnums.CurrencyCode;

  constructor(lowest?: number, highest?: number, currencyCode?: CurrencyEnums.CurrencyCode) {
    this.lowest = lowest;
    this.highest = highest;
    this.currencyCode = currencyCode;
  }
}

export class YearMonth {
  yearMonth?: number;
  formatter?: FormatDateOptions;

  constructor(yearMonth?: number, formatter?: FormatDateOptions) {
    this.yearMonth = yearMonth;
    this.formatter = formatter;
  }
}

export class YearAndMonth {
  year: number;
  month: number;
  formatter?: FormatDateOptions;

  constructor(year: number, month: number, formatter?: FormatDateOptions) {
    this.year = year;
    this.month = month;
    this.formatter = formatter;
  }
}

export interface ReloadState {
  entityId: number | string;
  reload?: boolean;
  markAsDone?: boolean;
}

export interface OrgNrRatingProps {
  orgNr: string;
}

export interface IconTitleProps {
  title: string;
  icon?: ReactElement;
  subtitle?: string;
  extraTags?: TagsProps[];
  defaultTagColor?: string;
  defaultTagSurface?: TagsProps["surface"];
}

export interface DashoardTitleProps {
  title: string;
  subtitle?: string;
  extraTags?: TagsProps[];
  defaultTagColor?: string;
  defaultTagSurface?: TagsProps["surface"];
}

export interface TitleIconProps {
  icon: IconDefinition;
  displayType?: StyleEnums.DisplayType;
}

export interface SubTitleWithIconProps extends ComponentBaseProps {
  icon: IconDefinition;
  subTitle: string;
}

export interface TitleSmallProps {
  title: string;
  className?: string;
}

export interface CountDownTimerProps {
  timer?: any;
  onCompleteCountDown?: () => void;
}

export interface CreditRatingProps {
  orgNr?: string;
  orgType: CommonEnums.OrganizationTypes;
}

export interface RatingInfo {
  ratingValue: number;
  ratingText: string;
  displayText: string;
  displayType: StyleEnums.DisplayType;
}

export interface WelcomeGuideTooltipProps {
  children?: ReactElement;
  placement: string;
  content?: string;
  open?: boolean;
}

export interface ReloadEventProps {
  reloadList: () => void;
  reloadKey: number;
}

export interface AddressProps {
  street1?: string;
  street2?: string;
  zipCode?: string;
  city?: string;
  country?: string;
}

export interface RangedAmount {
  dateRange: DateRange;
  amount: number;
}

export interface TitleWithAmountsProps {
  title: string;
  amounts: RangedAmount[];
}

export interface TitleWithDescriptionProps {
  title: string;
  helpText?: string;
  subTitle: string;
}

export interface TitleWithMonthRangeNavigatorProps {
  title: string | ReactElement;
  monthPickerSelections: MonthPickerQuickSelection[];
  className?: string;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
}

export interface RefreshButtonProps {
  onChange: (data: any) => void;
  onClick: () => Promise<string>;
}

export interface GridRProps extends ComponentBaseProps {
  id?: string;
  /**  Defines the align-content style property. It's applied for all screen sizes. */
  alignContent?: GridAlignContentType;
  /** Defines the align-items style property. It's applied for all screen sizes. */
  alignItems?: GridAlignItemsType;
  /**
   * Defines the `justify-content` style property.
   * It is applied for all screen sizes.
   */
  justifyContent?: GridJustifyContentType;
  /**
   * Override or extend the styles applied to the component.
   * See [CSS API](#css) below for more details.
   */
  classes?: Object;
  /**
   * If `true`, the component will have the flex *container* behavior.
   * You should be wrapping *items* with a *container*.
   */
  container?: boolean;
  /**
   * If `true`, the component will have the flex *item* behavior.
   * You should be wrapping *items* with a *container*.
   */
  item?: boolean;
  /**
   * Defines the `flex-direction` style property.
   * It is applied for all screen sizes.
   */
  direction?: GridDirection;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `xl` breakpoint and wider screens if not overridden.
   */
  xl?: boolean | GridSize;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `lg` breakpoint and wider screens if not overridden.
   */
  lg?: boolean | GridSize;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `md` breakpoint and wider screens if not overridden.
   */
  md?: boolean | GridSize;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `sm` breakpoint and wider screens if not overridden.
   */
  sm?: boolean | GridSize;
  /**
   * Defines the number of grids the component is going to use.
   * It's applied for the `xs` breakpoint and wider screens if not overridden.
   */
  xs?: boolean | GridSize;
  /** Defines the space between the type `item` component. */
  spacing?: GridSpacing;
  /** Defines the `flex-wrap` style property. */
  wrap?: GridWrap;
  /** If `true`, it sets `min-width: 0` on the item.  */
  zeroMinWidth?: boolean;

  style?: React.CSSProperties;
  /** If `true`, hide the element. */
  hidden?: boolean;
}

export interface SimpleTooltipProps {
  text?: React.ReactChild;
  children: React.ReactElement;
  showOnlyOnOverflow?: boolean;
  hidden?: boolean;
  placement?: "top" | "bottom" | "left" | "right";
}

export interface InfoTooltipProps {
  text: string;
  icon?: IconDefinition;
  placement?: "top" | "bottom" | "left" | "right";
}

export interface BannerFieldProps {
  label: string;
  value: string;
}

export interface NewReleaseWarningDialogProps {
  isOpen: boolean;
  isSubmitting?: boolean;
  displayType?: StyleEnums.DisplayType;
  onClick: () => void;
  startCountDown: () => void;
}

export interface NewReleaseTopBannerProps {
  displayType?: StyleEnums.DisplayType;
  onCompleteCountdown: () => void;
}

export interface ItemsSkeletonProps {
  columnsXs: number;
  columnsMd: number;
  rowCount?: number;
}

export interface TodoChipsListSkeletonProps extends ItemsSkeletonProps {
  totalItems: number;
}

export interface LinkifyProps {
  message: string;
}

export interface QuickActionButtonProps extends MoreActionMenuItem {
  /** If a secondary button such as cancel button */
  secondary?: boolean;
  /** Disable success messages */
  disbaleSuccessMessage?: boolean;
}

export interface WidgetBaseProps {
  onVisibilityChange?: (isHidden: boolean) => void;
}

export interface WidgetWithSwitchProps<T> extends WidgetBaseProps {
  switchType: T;
}

export interface WidgetSwitchProps<T> extends WidgetBaseProps {
  onSwitchTypeChanged: (type: T) => void;
}

export interface StatusIndicatorSmallProps {
  displayType?: StyleEnums.DisplayType;
}

export interface EntityCardSkeletonProps {
  /** If to show secondary section skeleton */
  hasInfoViewer?: boolean;
}

export interface SendEmailFormProps extends BaseFormViewComponentProps {
  emailSuggestions?: string[];
  disableSubject?: boolean;
  disableMessage?: boolean;
}

export interface TopCardContentSkeletonProps {
  hideTitle?: boolean;
  hideSubTitle?: boolean;
  hideDescriptionHeadingOne?: boolean;
  hideDescriptionHeadingTwo?: boolean;
  hideDescriptionheadingThree?: boolean;
  hideFooterText?: boolean;
  hasFooterButton?: boolean;
}

export interface LighterTitleProps {
  title: string | ReactElement;
  subTitle?: string | ReactElement;
}

export interface CircularLoadingWithTextProps {
  loadingText?: string;
}

export interface SimpleCommentProps {
  from?: User;
  isWint?: boolean;
  message: string;
}

export interface RatingBinaryProps {
  ratingTitle?: string;
  ratingPositiveText?: string;
  ratingNegativeText?: string;
  ratingPositiveIcon?: ReactElement;
  ratingNegativeIcon?: ReactElement;
  onRatingClick?: (rating: boolean) => void;
}

export interface ShowMoreButtonProps {
  children?: React.ReactNode;
  onClick: () => void;
  expandDirectionUp?: boolean;
}

export interface BlurBackdropProps {
  show?: boolean;
  onClick?: () => void;
}

export interface QuickLink {
  icon: IconDefinition;
  btnText: string;
  onClickHandler: () => void;
  hidden?: boolean;
}

export interface HighlightedAmountsTextProps {
  text: string;
}
